import * as types from '../mutation-types';
import ConversationAPI from '../../api/inbox/conversation';

const TYPING_DRAFTS_STORAGE_KEY = 'chatwoot_typing_drafts_v1';
const TYPING_DRAFT_TTL_MS = 30 * 60 * 1000;

const normalizeDraftEntry = draft => {
  if (typeof draft === 'string') {
    const content = draft.slice(0, 300).trim();
    return content ? { content, updated_at: Date.now() } : null;
  }

  if (!draft || typeof draft !== 'object') {
    return null;
  }

  const content = String(draft.content || '').slice(0, 300).trim();
  const updatedAt = Number(draft.updated_at || 0);
  if (!content || !updatedAt) {
    return null;
  }

  return { content, updated_at: updatedAt };
};

const pruneExpiredDrafts = drafts => {
  const now = Date.now();
  const nextDrafts = {};

  Object.entries(drafts || {}).forEach(([conversationId, draft]) => {
    const normalizedDraft = normalizeDraftEntry(draft);
    if (!normalizedDraft) {
      return;
    }

    if (now - normalizedDraft.updated_at > TYPING_DRAFT_TTL_MS) {
      return;
    }

    nextDrafts[conversationId] = normalizedDraft;
  });

  return nextDrafts;
};

const loadPersistedDrafts = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(TYPING_DRAFTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? pruneExpiredDrafts(parsed) : {};
  } catch (error) {
    return {};
  }
};

const persistDrafts = drafts => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const validDrafts = pruneExpiredDrafts(drafts);
    window.localStorage.setItem(
      TYPING_DRAFTS_STORAGE_KEY,
      JSON.stringify(validDrafts)
    );
  } catch (error) {
    // Ignore persistence errors caused by quota or privacy mode.
  }
};

const state = {
  records: {},
  drafts: loadPersistedDrafts(),
};

export const getters = {
  getUserList: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getDraftPreview: $state => id => {
    const draft = normalizeDraftEntry($state.drafts[Number(id)]);
    if (!draft) {
      return '';
    }

    if (Date.now() - draft.updated_at > TYPING_DRAFT_TTL_MS) {
      return '';
    }

    return draft.content;
  },
};

export const actions = {
  toggleTyping: async (_, { status, conversationId, isPrivate }) => {
    try {
      await ConversationAPI.toggleTyping({ status, conversationId, isPrivate });
    } catch (error) {
      // Handle error
    }
  },
  create: ({ commit }, { conversationId, user, typingContent = '' }) => {
    commit(types.default.ADD_USER_TYPING_TO_CONVERSATION, {
      conversationId,
      user,
      typingContent,
    });
  },
  destroy: ({ commit }, { conversationId, user }) => {
    commit(types.default.REMOVE_USER_TYPING_FROM_CONVERSATION, {
      conversationId,
      user,
    });
  },
  clearDraft: ({ commit }, { conversationId }) => {
    commit(types.default.CLEAR_TYPING_DRAFT_FOR_CONVERSATION, { conversationId });
  },
};

export const mutations = {
  [types.default.ADD_USER_TYPING_TO_CONVERSATION]: (
    $state,
    { conversationId, user, typingContent }
  ) => {
    const records = $state.records[conversationId] || [];
    const normalizedTypingContent = String(typingContent || '').slice(0, 300);
    const userIndex = records.findIndex(
      record => record.id === user.id && record.type === user.type
    );

    if (normalizedTypingContent) {
      $state.drafts = {
        ...$state.drafts,
        [conversationId]: {
          content: normalizedTypingContent,
          updated_at: Date.now(),
        },
      };
      persistDrafts($state.drafts);
    }

    if (userIndex !== -1) {
      const updatedRecords = [...records];
      updatedRecords[userIndex] = {
        ...updatedRecords[userIndex],
        typing_content: normalizedTypingContent,
      };
      $state.records = {
        ...$state.records,
        [conversationId]: updatedRecords,
      };
      return;
    }

    $state.records = {
      ...$state.records,
      [conversationId]: [
        ...records,
        {
          ...user,
          typing_content: normalizedTypingContent,
        },
      ],
    };
  },
  [types.default.REMOVE_USER_TYPING_FROM_CONVERSATION]: (
    $state,
    { conversationId, user }
  ) => {
    const records = $state.records[conversationId] || [];
    const updatedRecords = records.filter(
      record => record.id !== user.id || record.type !== user.type
    );
    $state.records = {
      ...$state.records,
      [conversationId]: updatedRecords,
    };
  },
  [types.default.CLEAR_TYPING_DRAFT_FOR_CONVERSATION]: (
    $state,
    { conversationId }
  ) => {
    const updatedDrafts = { ...$state.drafts };
    delete updatedDrafts[conversationId];
    $state.drafts = updatedDrafts;
    persistDrafts($state.drafts);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
